<template>
  <div class="verifikator-table">
    <label class="section-label">Verifikator Surat</label>
    <p class="section-description">
      Tambahkan semua pengurus yang perlu memverifikasi surat ini sebelum surat ditandatangani.
      Pengurus dengan nomor urut paling kecil akan paling dahulu dimintai verifikasi surat.
    </p>
    <div class="table-wrapper">
      <a-table
        :columns="computedColumns"
        :data-source="dataSource"
        :pagination="false"
        size="small"
        class="table-component"
        row-key="id"
      >
        <template #noUrut="{ index }">
          {{ index + 1 }}
        </template>
        <template #namaPengurus="{ record }">
          <a-select
            v-model:value="record.userId"
            placeholder="Cari nama pengurus"
            :options="pengurusOptions"
            style="width: 100%"
            show-search
            :filter-option="filterOption"
            :disabled="disabled || isLockedRow(record)"
            @change="handleChange"
          />
        </template>
        <template #jabatan="{ record }">
          {{ getJabatan(record.userId) }}
        </template>
        <template #npaPerki="{ record }">
          {{ getNpa(record.userId) }}
        </template>
        <template #aksi="{ record, index }">
          <button
            v-if="!hideDeleteForLocked || !isLockedRow(record)"
            type="button"
            class="delete-button"
            @click.prevent="handleRemove(index)"
            title="Hapus file"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="3 6 5 6 21 6"></polyline>
              <path
                d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
              ></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
          <span v-else class="locked-indicator">
            <LockOutlined />
          </span>
        </template>
      </a-table>
    </div>
    <a-button v-if="!disabled" type="link" class="btn-tambah-baris" @click="handleAdd">
      <PlusOutlined /> TAMBAH BARIS
    </a-button>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'
import { useStore } from 'vuex'
import { DeleteOutlined, PlusOutlined, LockOutlined } from '@ant-design/icons-vue'

export default defineComponent({
  name: 'VerifikatorTable',
  components: {

    PlusOutlined,
    LockOutlined,
  },
  props: {
    dataSource: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    lockedIds: {
      type: Array,
      default: () => [],
    },
    hideDeleteForLocked: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['add', 'remove', 'change'],
  setup(props, { emit }) {
    const store = useStore()

    // Filter out admin, super_admin, and pekerja_harian roles - they should not be verifikator
    const pengurusOptions = computed(() => {
      const options = store.state.suratKeluar.pengurusOptions || []
      const pengurusMap = store.state.suratKeluar.pengurusMap || {}
      return options.filter(option => {
        const user = pengurusMap[option.value]
        if (user && user.role) {
          const role = user.role.toLowerCase()
          return role !== 'admin' && role !== 'super_admin' && role !== 'pekerja_harian'
        }
        return true
      })
    })

    // Check if a row is locked (existing verifikator that shouldn't be modified)
    const isLockedRow = (record) => {
      if (!props.hideDeleteForLocked) return false
      // Check if the record id is in lockedIds
      return props.lockedIds.includes(record.id) || props.lockedIds.includes(record.verifikatorId)
    }

    const computedColumns = computed(() => {
      const baseColumns = [
        {
          title: 'No. Urut',
          key: 'noUrut',
          width: '7%',
          slots: { customRender: 'noUrut' },
        },
        {
          title: 'Nama Pengurus',
          dataIndex: 'namaPengurus',
          key: 'namaPengurus',
          width: '30%',
          slots: { customRender: 'namaPengurus' },
        },
        {
          title: 'Jabatan',
          dataIndex: 'jabatan',
          key: 'jabatan',
          width: '25%',
          slots: { customRender: 'jabatan' },
        },
        {
          title: 'NPA PERKI',
          dataIndex: 'npaPerki',
          key: 'npaPerki',
          width: '25%',
          slots: { customRender: 'npaPerki' },
        },
        {
          title: 'Aksi',
          key: 'aksi',
          width: '10%',
          slots: { customRender: 'aksi' },
        },
      ]
      return baseColumns
    })

    const filterOption = (input, option) => {
      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    const getJabatan = userId => {
      return store.getters['suratKeluar/getJabatanByUserId'](userId)
    }

    const getNpa = userId => {
      return store.getters['suratKeluar/getNpaByUserId'](userId)
    }

    const handleAdd = () => {
      emit('add')
    }

    const handleRemove = index => {
      emit('remove', index)
    }

    const handleChange = () => {
      emit('change')
    }

    return {
      computedColumns,
      pengurusOptions,
      filterOption,
      getJabatan,
      getNpa,
      handleAdd,
      handleRemove,
      handleChange,
      isLockedRow,
    }
  },
})
</script>

<style lang="scss" scoped>
.verifikator-table {
  margin-bottom: 24px;

  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin-bottom: 8px;

    @media (max-width: 768px) {
      overflow-x: auto;
    }
  }

  .section-label {
    display: block;
    font-weight: 500;
    color: #262626;
    margin-bottom: 8px;
    font-size: 14px;
  }

  .section-description {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 12px;
  }

  .table-component {
    min-width: 850px;

    @media (max-width: 768px) {
      min-width: 1050px;
    }

    :deep(.ant-table-thead > tr > th) {
      background: #fafafa;
      font-weight: 600;
      font-size: 12px;
      color: #8c8c8c;
    }
  }

  .btn-tambah-baris {
    color: #262626;
    font-weight: 700;
    margin-bottom: 16px;
    font-size: 14px;
    padding: 0;
    height: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .delete-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 2px;
    transition: all 0.2s;
    color: #ff4d4f;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      background: #fff1f0;
    }
  }

  .locked-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    color: #8c8c8c;
    font-size: 14px;
  }
}


</style>
